import type { UploadFileCategory } from "./ports";
import { getInvalidMultipartTargetIdentityField, hasValidMultipartUrls, isValidPresignedUrl } from "./multipartValidation";
import { CHUNK_SIZE } from "./chunkSize";
import { processWithConcurrency } from "./concurrency";
import { computeRetryAt } from "./retryPolicy";

export const INITIATE_CONCURRENCY = 5;

/**
 * LSP-19605: pacing for the best-effort rollback that aborts every already-minted target when one
 * category's initiation fails.
 *
 * The rollback used to fan out one abort per minted target with no bound and no retry. Under the
 * 2026-09-04 API saturation (H12 timeouts) a 100-file batch whose initiation died mid-way fired 100
 * aborts at once, they all timed out together, and one tab logged the same error 100 times in 12 ms
 * — while piling 100 more requests onto an API that was already refusing work.
 *
 * What replaces it:
 *  - Aborts run at the SAME bounded concurrency as initiation (`concurrency`, default
 *    `INITIATE_CONCURRENCY`), so a rollback can never out-fan the initiation it is undoing.
 *  - A retryable abort failure (network, timeout, 5xx, 408, 429) is retried with the shared
 *    exponential-with-jitter curve, capped at `ROLLBACK_ABORT_MAX_ATTEMPTS`. A definitive 4xx (a 404
 *    for a target already gone, a 409 fence) is not retried — the server has answered.
 *  - The whole rollback gets `ROLLBACK_BUDGET_MS` of the caller's time. Targets not yet attempted
 *    when the budget runs out are left to the server's incomplete-parts sweep (the deferral path
 *    that already reclaims orphaned multipart uploads) and the initiation error surfaces to the
 *    user on time instead of waiting behind minutes of timed-out aborts. Attempts already in flight
 *    finish in the background and are counted in the summary.
 *  - One summary line per rollback (`reportRollbackFailures`) instead of one error per attempt —
 *    emitted exactly once, when the rollback settles or at the budget, whichever comes first.
 */
export const ROLLBACK_ABORT_MAX_ATTEMPTS = 3;
export const ROLLBACK_ABORT_BASE_DELAY_MS = 500;
export const ROLLBACK_ABORT_MAX_DELAY_MS = 2_000;
export const ROLLBACK_BUDGET_MS = 10_000;

/** One target the rollback could not abort, with the last failure and how hard it tried. */
export interface RollbackAbortFailure<Target> {
	target: Target;
	error: unknown;
	attempts: number;
	/** The definitive HTTP status when the server answered; absent for network failures and timeouts. */
	httpStatus?: number;
}

/** The single per-rollback report. Emitted only when at least one target was not confirmed aborted. */
export interface RollbackFailureSummary<Target> {
	abortedCount: number;
	failed: readonly RollbackAbortFailure<Target>[];
	/** Targets never attempted because `budgetMs` ran out first; the server sweep reclaims them. */
	unattempted: readonly Target[];
	/**
	 * Targets whose abort was still awaiting a server answer when the budget fired; only non-empty on a
	 * budget summary. Named individually because their eventual outcome is never reported — the summary
	 * fires once — so this is the one place the possibly-orphaned target ids appear.
	 */
	inFlight: readonly Target[];
	elapsedMs: number;
	budgetMs: number;
	maxAttempts: number;
}

/** Test seams for the rollback pacing; production callers take the defaults above. */
export interface RollbackPacingOptions {
	budgetMs?: number;
	maxAttempts?: number;
	baseDelayMs?: number;
	maxDelayMs?: number;
}
export const MAX_MULTIPART_PARTS = 10_000;

/**
 * THE part-count rule for "can this file be uploaded at all", in one place.
 *
 * LSP-19397: the upload ceiling is a PRODUCT of two numbers — this part limit and the runtime chunk
 * size — and nothing screened a file against it before the initiate request went out. A customer's
 * 5+ GB video was refused by the API with its own raw `totalChunks must be an integer between 1 and
 * 1000.`, which names an internal wire field, never states the file size or the limit, and reached
 * the user only AFTER they committed the upload and after the conversation row had been created
 * (leaving an orphan behind). Admin's file validation had already reported the same file "valid".
 *
 * So the pre-flight screens (`useFileUploadFlow`, `ActionUploadPhoto`) and `initiateCategory`'s own
 * refusal below both go through these functions. That is the whole point: a permissive picker and a
 * strict writer encoding the boundary separately is the defect, and one rule is the fix. The chunk
 * size is a parameter because the engine works from its session's `options.chunkSize` while a
 * pre-flight screen only has the deployment default.
 */
export function requiredMultipartParts(fileSizeBytes: number, chunkSizeBytes: number = CHUNK_SIZE): number {
	return Math.ceil(fileSizeBytes / chunkSizeBytes);
}

/** The largest single file `MAX_MULTIPART_PARTS` parts can describe at the given chunk size. */
export function maxUploadableBytes(chunkSizeBytes: number = CHUNK_SIZE): number {
	return MAX_MULTIPART_PARTS * chunkSizeBytes;
}

export function exceedsMultipartPartLimit(fileSizeBytes: number, chunkSizeBytes: number = CHUNK_SIZE): boolean {
	// A non-positive or non-finite size is NOT reported as "too large": `initiateCategory` already
	// refuses those with its own "requires a positive finite file size" error, and answering them
	// here would replace a precise diagnosis with a misleading one.
	if (!Number.isFinite(fileSizeBytes) || fileSizeBytes <= 0) return false;
	return requiredMultipartParts(fileSizeBytes, chunkSizeBytes) > MAX_MULTIPART_PARTS;
}

/**
 * Copy-facing size label, and deliberately not one of the two existing formatters:
 *   - `formatFileSize` (index.ts) stops at MB and is pinned by its own break-it specs, so widening
 *     it would change every upload log and label in both apps; and a limit rendered as
 *     "50000.0 MB" is no more actionable than the wire error this message replaces.
 *   - `@common-ui/utilities/browserDetection` has a private GB-capable twin for the large-file
 *     warning, but it lives in the UI package. Reaching for it would make the uploads package
 *     depend on the UI package for four lines of arithmetic.
 */
function describeBytesForCustomer(bytes: number, rounding: "up" | "down"): string {
	// break-it round 1: rounding BOTH figures to nearest produced "it's 48.8 GB, and the most we can
	// take in one upload is 48.8 GB" for a file one byte over the ceiling — a refusal that reads as
	// self-contradictory. Rounding the file UP and the limit DOWN makes a collision impossible rather
	// than unlikely: floor(limit) <= limit < fileSize <= ceil(fileSize), so the two can never print
	// the same number however close they are. Both directions are also the honest ones — never
	// understate what the customer handed us, never promise more capacity than we have.
	const round = rounding === "up" ? Math.ceil : Math.floor;
	const gigabytes = bytes / (1024 * 1024 * 1024);
	if (gigabytes >= 1) return `${(round(gigabytes * 10) / 10).toFixed(1)} GB`;
	return `${round(bytes / (1024 * 1024))} MB`;
}

/**
 * What the user is told when their file cannot be described by a single multipart upload.
 *
 * Deliberately warm and blame-free, and shown as a WARNING rather than an error: picking a big video
 * is not a mistake the customer made. Voice matches the house pattern in
 * `apps/contact-admin/src/components/MediaLightBox.vue` ("It looks like something went wrong … contact
 * our support team at …") and `apps/contact-admin/CLAUDE.md` → "User-Facing Copy". It states the size
 * we can take, gives an action that actually works (a smaller file — retrying the SAME file cannot
 * succeed), and offers a human. The support address is a parameter because this package is
 * app-agnostic; callers pass their own `VITE_SUPPORT_EMAIL`.
 */
export function buildFileTooLargeForUploadMessage(
	fileName: string,
	fileSizeBytes: number,
	supportEmail: string,
	chunkSizeBytes: number = CHUNK_SIZE
): string {
	return (
		`"${fileName}" is too big to upload — it's ${describeBytesForCustomer(fileSizeBytes, "up")}, and the most we can ` +
		`take in one upload is ${describeBytesForCustomer(maxUploadableBytes(chunkSizeBytes), "down")}. You can try again ` +
		`with a smaller file, or contact our support team at ${supportEmail} and we'll help you get it uploaded.`
	);
}

type FileUploadCategory = Exclude<UploadFileCategory, "video">;
export type UploadTargetCategory = UploadFileCategory;

export interface UploadFileLike {
	name: string;
	size: number;
	type: string;
}

export interface MultipartUploadTarget {
	type: "multipart";
	uploadId: string;
	s3Key: string;
	bucket: string;
	chunkUrls: string[];
}

/**
 * The two wire families share the upload engine but not their storage identity.
 * Legacy rows carry S3 coordinates; `/uploads` rows carry only the opaque
 * registry/attempt identity and the server-issued target envelopes.
 */
export type UploadEndpointFamily = "legacy" | "uploads";
export const DEFAULT_UPLOAD_ENDPOINT_FAMILY: UploadEndpointFamily = "legacy";

export interface UploadTargetEnvelope {
	targetId: string;
	attemptId: string;
	generation: number;
	ordinal: number;
	url: string;
	headers: Readonly<Record<string, string>>;
	expiresAt: string;
	expectedBytes: number | null;
	expectedDigest: string | null;
}

export interface LegacyMultipartUploadTarget extends MultipartUploadTarget {
	family: "legacy";
}

export interface UploadsMultipartUploadTarget {
	type: "multipart";
	family: "uploads";
	registryId: string;
	attemptId: string;
	generation: number;
	takeGeneration?: string;
	envelopes: readonly UploadTargetEnvelope[];
	/**
	 * The transport the facade minted for this attempt. Absent on every row persisted before plan 3a
	 * and on every pass-through that re-normalizes an existing target, which both mean "multipart" —
	 * read it through `uploadsTargetTransport` rather than comparing this field directly.
	 */
	transport?: UploadsMintedTransport;
}

/** The transport the facade actually minted for an `/uploads` target. Absent on rows persisted before plan 3a → "multipart". */
export type UploadsMintedTransport = "multipart" | "chunked-object";

/**
 * The minted transport of a target, defaulting an absent/unknown value to "multipart". Only
 * "chunked-object" changes what a completion body must look like, so anything else — including a
 * legacy row that never carried the field — is the multipart lane.
 */
export function uploadsTargetTransport(target: { transport?: string }): UploadsMintedTransport {
	return target.transport === "chunked-object" ? "chunked-object" : "multipart";
}

export type FamilyAwareMultipartUploadTarget = MultipartUploadTarget | LegacyMultipartUploadTarget | UploadsMultipartUploadTarget;
export type UploadCompletionPart = { ETag: string; PartNumber: number };
export type UploadsCompletionPart = UploadCompletionPart & { targetId: string; ordinal: number };
export type LegacyStorageCoordinates = Pick<MultipartUploadTarget, "uploadId" | "s3Key" | "bucket">;

export function uploadTargetFamily(target: MultipartUploadTarget | FamilyAwareMultipartUploadTarget): UploadEndpointFamily {
	const familyDescriptor = Object.getOwnPropertyDescriptor(target, "family");
	if (familyDescriptor && !("value" in familyDescriptor)) throw new Error("Upload target family must be a data property");
	return familyDescriptor?.value === "uploads" ? "uploads" : "legacy";
}

export function isUploadsUploadTarget(
	target: MultipartUploadTarget | FamilyAwareMultipartUploadTarget
): target is UploadsMultipartUploadTarget {
	return uploadTargetFamily(target) === "uploads";
}

/*
 * `uploadTargetChunkUrls` / `uploadTargetChunkHeaders` / `uploadTargetOpaqueId` are the SANCTIONED
 * facade for call sites that hold no target-holding scope — a one-off read of a different target
 * expression each time (the twelve such call sites are all in Vue SFCs). They are NOT dead
 * duplication of the adapter methods and must not be "finished off" by forcing every caller through
 * `resolveUploadTargetAdapter`: doing so would double-evaluate each target expression and push a
 * resolver copy into template code. Prefer `resolveUploadTargetAdapter` wherever ONE target does
 * bind a scope; reach for these where it does not.
 */
export function uploadTargetChunkUrls(target: MultipartUploadTarget | FamilyAwareMultipartUploadTarget): readonly string[] {
	return uploadTargetFamily(target) === "uploads"
		? createUploadTargetAdapter("uploads").chunkUrls(target as UploadsMultipartUploadTarget)
		: createUploadTargetAdapter("legacy").chunkUrls(target as MultipartUploadTarget);
}

/** Exact server-signed headers, aligned with the zero-based client chunk index. */
export function uploadTargetChunkHeaders(
	target: MultipartUploadTarget | FamilyAwareMultipartUploadTarget
): readonly Readonly<Record<string, string>>[] {
	return uploadTargetFamily(target) === "uploads"
		? createUploadTargetAdapter("uploads").chunkHeaders(target as UploadsMultipartUploadTarget)
		: createUploadTargetAdapter("legacy").chunkHeaders(target as MultipartUploadTarget);
}

/** The durable row shape every `/uploads` target rebuild reads. */
export interface PersistedUploadTargetRow {
	endpointFamily?: UploadEndpointFamily;
	registryId?: string;
	attemptId?: string;
	generation?: number;
	takeGeneration?: string;
	targetEnvelopesJson?: string;
	/** The minted transport. Threaded through EVERY rebuild — see the note below. */
	transport?: string;
}

/**
 * Revalidates durable `/uploads` envelope data and re-mints the target a reload/resume replays.
 *
 * The row's `transport` is threaded in deliberately: a rebuild that reads only
 * `targetEnvelopesJson` produces a target with no transport, which
 * `uploadsTargetTransport` then reads as "multipart" — silently demoting a resumed
 * chunked-object attempt whose completion body differs. Every site that rebuilds a target from
 * `targetEnvelopesJson` goes through here for exactly that reason.
 */
export function persistedUploadTarget(session: PersistedUploadTargetRow): UploadsMultipartUploadTarget | undefined {
	if (session.endpointFamily !== "uploads") return undefined;
	let envelopes: unknown;
	try {
		envelopes = JSON.parse(session.targetEnvelopesJson ?? "");
	} catch (error) {
		// `cause` is load-bearing: driverUrlRefresh re-wraps this as MultipartUrlRefreshError and its
		// `originalError` is what the diagnostic log prints — without it the parse error is lost.
		throw new Error("Persisted uploads target envelopes are invalid", { cause: error });
	}
	return createUploadsMultipartUploadTarget({
		registryId: session.registryId ?? "",
		attemptId: session.attemptId ?? "",
		generation: session.generation ?? 0,
		takeGeneration: session.takeGeneration ?? "",
		envelopes: Array.isArray(envelopes) ? (envelopes as UploadTargetEnvelope[]) : [],
		transport: session.transport
	});
}

/** Revalidates durable `/uploads` envelope data before a reload/resume replays it. */
export function persistedUploadTargetChunkHeaders(
	session: PersistedUploadTargetRow
): readonly Readonly<Record<string, string>>[] | undefined {
	return persistedUploadTarget(session)?.envelopes.map((envelope) => envelope.headers);
}

/** The value used by the durable client row for dedupe/ownership. It is never an S3 coordinate. */
export function uploadTargetOpaqueId(target: MultipartUploadTarget | FamilyAwareMultipartUploadTarget): string {
	return uploadTargetFamily(target) === "uploads"
		? createUploadTargetAdapter("uploads").opaqueId(target as UploadsMultipartUploadTarget)
		: createUploadTargetAdapter("legacy").opaqueId(target as MultipartUploadTarget);
}

export class UploadTargetFamilyMismatchError extends Error {
	constructor(expected: UploadEndpointFamily, actual: UploadEndpointFamily) {
		super(`Upload target family does not match the pinned session family (expected ${expected}, received ${actual})`);
		this.name = "UploadTargetFamilyMismatchError";
	}
}

function assertNonBlank(value: unknown, field: string): asserts value is string {
	if (typeof value !== "string" || value.trim().length === 0) throw new Error(`Upload target has invalid ${field}`);
}

function assertPositiveInteger(value: unknown, field: string): asserts value is number {
	if (!Number.isSafeInteger(value) || (value as number) < 1) throw new Error(`Upload target has invalid ${field}`);
}

function assertIsoTimestamp(value: unknown, field: string): asserts value is string {
	assertNonBlank(value, field);
	const timestamp = Date.parse(value);
	if (!Number.isFinite(timestamp) || new Date(timestamp).toISOString() !== value) {
		throw new Error(`Upload target has invalid ${field}`);
	}
}

function isBrowserControlledHeader(name: string): boolean {
	return /^(?:accept-charset|accept-encoding|access-control-request-(?:headers|method)|authorization|connection|content-length|cookie2?|date|dnt|expect|host|keep-alive|origin|referer|set-cookie|te|trailer|transfer-encoding|upgrade|via|proxy-|sec-)/i.test(
		name
	);
}

function isBrowserSettableHeaderValue(value: unknown): value is string {
	return typeof value === "string" && /^[\t\x20-\x7e\x80-\xff]*$/.test(value) && !/^[\t ]|[\t ]$/.test(value);
}

/** Ensures a server-signed header map can be replayed verbatim by XMLHttpRequest. */
export function isValidBrowserSignedHeaders(value: unknown): value is Readonly<Record<string, string>> {
	if (!value || typeof value !== "object" || Array.isArray(value)) return false;
	const prototype = Object.getPrototypeOf(value);
	if (prototype !== Object.prototype && prototype !== null) return false;
	if (Object.getOwnPropertySymbols(value).length > 0) return false;
	const signedHeaderNames = new Set<string>();
	return Object.keys(value).every((name) => {
		const descriptor = Object.getOwnPropertyDescriptor(value, name);
		if (!descriptor || !("value" in descriptor)) return false;
		const headerValue = descriptor.value;
		const normalizedName = name.toLowerCase();
		if (
			!/^[!#$%&'*+\-.^_`|~0-9A-Za-z]+$/.test(name) ||
			!isBrowserSettableHeaderValue(headerValue) ||
			signedHeaderNames.has(normalizedName) ||
			isBrowserControlledHeader(normalizedName)
		) {
			return false;
		}
		signedHeaderNames.add(normalizedName);
		return true;
	});
}

/**
 * Reads one own data property, refusing an accessor. Exported so lanes that must read a
 * field the envelope contract does not own (for example the Live snapshot `registryId`)
 * use the same no-getter read as the envelope validator instead of copying it.
 */
export function readUploadTargetDataProperty(source: object, field: string): unknown {
	const descriptor = Object.getOwnPropertyDescriptor(source, field);
	if (!descriptor || !("value" in descriptor)) throw new Error(`Upload target envelope must not carry accessor property ${field}`);
	return descriptor.value;
}

function copyBrowserSignedHeaders(value: Readonly<Record<string, string>>): Readonly<Record<string, string>> {
	const copy: Record<string, string> = {};
	for (const name of Object.keys(value)) {
		const descriptor = Object.getOwnPropertyDescriptor(value, name);
		if (!descriptor || !("value" in descriptor)) throw new Error("Upload target envelope headers must not carry accessor properties");
		copy[name] = descriptor.value;
	}
	return copy;
}

/** Strictly copy and fence one server-issued target envelope before it enters durable client state. */
export function validateUploadTargetEnvelope(
	envelope: UploadTargetEnvelope,
	expectedOrdinal: number,
	expectedAttemptId: string,
	expectedGeneration: number
): UploadTargetEnvelope {
	const targetId = readUploadTargetDataProperty(envelope, "targetId");
	const attemptId = readUploadTargetDataProperty(envelope, "attemptId");
	const generation = readUploadTargetDataProperty(envelope, "generation");
	const ordinal = readUploadTargetDataProperty(envelope, "ordinal");
	const url = readUploadTargetDataProperty(envelope, "url");
	const headers = readUploadTargetDataProperty(envelope, "headers");
	const expiresAt = readUploadTargetDataProperty(envelope, "expiresAt");
	const expectedBytes = readUploadTargetDataProperty(envelope, "expectedBytes");
	const expectedDigest = readUploadTargetDataProperty(envelope, "expectedDigest");
	assertNonBlank(targetId, "targetId");
	assertNonBlank(attemptId, "attemptId");
	assertPositiveInteger(generation, "generation");
	assertPositiveInteger(ordinal, "ordinal");
	assertNonBlank(url, "url");
	assertIsoTimestamp(expiresAt, "expiresAt");
	if (!isValidBrowserSignedHeaders(headers)) {
		// These values are part of the server-signed request. Coercing runtime
		// response data (for example true -> "true") would replay a different
		// request and make a malformed envelope look like an S3 failure.
		throw new Error(`Upload target envelope ${expectedOrdinal} has invalid headers`);
	}
	if (ordinal !== expectedOrdinal || attemptId !== expectedAttemptId || generation !== expectedGeneration) {
		throw new Error("Upload target envelopes must be contiguous and share one attempt generation");
	}
	if (!isValidPresignedUrl(url)) throw new Error(`Upload target envelope ${expectedOrdinal} has an invalid URL`);
	if (expectedBytes !== null && (!Number.isSafeInteger(expectedBytes) || (expectedBytes as number) < 0)) {
		throw new Error(`Upload target envelope ${expectedOrdinal} has invalid expectedBytes`);
	}
	if (expectedDigest !== null && (typeof expectedDigest !== "string" || !/^[a-f0-9]{64}$/.test(expectedDigest))) {
		throw new Error(`Upload target envelope ${expectedOrdinal} has invalid expectedDigest`);
	}
	return {
		targetId,
		attemptId,
		generation,
		ordinal,
		url,
		headers: copyBrowserSignedHeaders(headers),
		expiresAt,
		expectedBytes: expectedBytes as number | null,
		expectedDigest: expectedDigest as string | null
	};
}

function assertNoLegacyStorageCoordinates(input: unknown): void {
	const visited = new Set<object>();
	const legacyCoordinateFields = ["uploadId", "s3Key", "bucket", "multipartUploadId"] as const;
	const descriptorInPrototypeChain = (candidate: object, field: string): PropertyDescriptor | undefined => {
		let prototype: object | null = candidate;
		while (prototype) {
			const descriptor = Object.getOwnPropertyDescriptor(prototype, field);
			if (descriptor) return descriptor;
			prototype = Object.getPrototypeOf(prototype);
		}
		return undefined;
	};
	const inspect = (candidate: unknown): string | undefined => {
		// Runtime callers can attach compatibility metadata to callable wrappers too. Treat
		// functions as traversable objects so a legacy coordinate cannot hide behind a
		// function-valued property and cross the opaque `/uploads` boundary.
		if (!candidate || (typeof candidate !== "object" && typeof candidate !== "function")) return undefined;
		if (visited.has(candidate)) return undefined;
		visited.add(candidate);
		const isFunction = typeof candidate === "function";

		for (const coordinateField of legacyCoordinateFields) {
			// Check the prototype chain as well as own properties. A runtime response or a
			// persisted object can be reconstructed with an inherited field; accepting that
			// object would let a legacy storage identity cross the uploads-family boundary.
			if (descriptorInPrototypeChain(candidate, coordinateField)) return coordinateField;
		}
		if (isFunction) {
			// A callable compatibility wrapper can be a Proxy whose configurable own
			// properties are hidden from ownKeys/getOwnPropertyDescriptor. There is no
			// reflective API that can recover those hidden properties, so fail closed for
			// the wrapper itself instead of allowing a legacy coordinate to cross the
			// opaque boundary undetected.
			return "s3Key";
		}

		// Reject coordinate aliases hidden inside a compatibility wrapper too. This is
		// intentionally a small recursive walk over object values, with cycle protection,
		// so nested/prototype smuggling cannot be silently accepted as an unknown field.
		for (const key of Object.getOwnPropertyNames(candidate)) {
			const descriptor = Object.getOwnPropertyDescriptor(candidate, key);
			// Every ordinary function carries legacy engine accessors named `caller` and
			// `arguments`; they are runtime metadata, not caller-supplied coordinates.
			if (isFunction && (key === "caller" || key === "arguments") && descriptor && !("value" in descriptor)) continue;
			if (!descriptor || !("value" in descriptor)) throw new Error("Uploads-family target must not carry accessor properties");
			const nestedCoordinateField = inspect(descriptor.value);
			if (nestedCoordinateField) return nestedCoordinateField;
		}
		for (const key of Object.getOwnPropertySymbols(candidate)) {
			const descriptor = Object.getOwnPropertyDescriptor(candidate, key);
			if (!descriptor || !("value" in descriptor)) throw new Error("Uploads-family target must not carry accessor properties");
			const nestedCoordinateField = inspect(descriptor.value);
			if (nestedCoordinateField) return nestedCoordinateField;
		}
		let prototype = Object.getPrototypeOf(candidate);
		while (prototype && prototype !== Object.prototype && prototype !== Function.prototype) {
			for (const key of Object.getOwnPropertyNames(prototype)) {
				const descriptor = Object.getOwnPropertyDescriptor(prototype, key);
				if (descriptor && !("value" in descriptor)) throw new Error("Uploads-family target must not carry accessor properties");
			}
			for (const key of Object.getOwnPropertySymbols(prototype)) {
				const descriptor = Object.getOwnPropertyDescriptor(prototype, key);
				if (!descriptor || !("value" in descriptor)) throw new Error("Uploads-family target must not carry accessor properties");
				// Built-in prototypes expose callable symbol methods (for example
				// Array.prototype[Symbol.iterator]); they are not caller-supplied
				// compatibility wrappers and must not make every valid envelope fail closed.
				if (typeof descriptor.value === "function") continue;
				const nestedCoordinateField = inspect(descriptor.value);
				if (nestedCoordinateField) return nestedCoordinateField;
			}
			prototype = Object.getPrototypeOf(prototype);
		}

		if (candidate instanceof Map) {
			for (const [key, value] of Map.prototype.entries.call(candidate)) {
				const nestedCoordinateField = inspect(key) ?? inspect(value);
				if (nestedCoordinateField) return nestedCoordinateField;
			}
		}
		if (candidate instanceof Set) {
			for (const value of Set.prototype.values.call(candidate)) {
				const nestedCoordinateField = inspect(value);
				if (nestedCoordinateField) return nestedCoordinateField;
			}
		}
		return undefined;
	};

	const coordinateField = inspect(input);
	if (coordinateField) throw new Error(`Uploads-family target must not carry ${coordinateField} (legacy coordinate)`);
}

export function createLegacyMultipartUploadTarget(target: MultipartUploadTarget): LegacyMultipartUploadTarget {
	if (target.type !== "multipart") throw new Error("Upload target has invalid type");
	assertNonBlank(target.uploadId, "uploadId");
	assertNonBlank(target.s3Key, "s3Key");
	assertNonBlank(target.bucket, "bucket");
	if (!Array.isArray(target.chunkUrls) || target.chunkUrls.length === 0 || target.chunkUrls.some((url) => !isValidPresignedUrl(url))) {
		throw new Error("Upload target has invalid chunk URLs");
	}
	return { ...target, family: "legacy" };
}

/**
 * Every `/uploads` create response DECLARES the transport the server actually minted, and every
 * client then wraps that response as a MULTIPART target. A video minted as `kind: "recording"` with
 * `supportedTransports: ["chunk-stream"]` therefore became a multipart target whose completion posts
 * a parts manifest to a chunk-stream session: a permanent refusal, with no trace at the moment the
 * wrong assumption was made.
 *
 * `chunked-object` is the one non-multipart transport this constructor now CARRIES rather than
 * discards (plan 3a): its targets are ordinary per-ordinal PUT envelopes — the same wire the
 * multipart lane uses — and only the COMPLETION body differs, so the transport has to survive onto
 * the target for `uploadsTargetTransport` to pick the right completion at the end. Everything else
 * still only warns.
 *
 * Deliberately a WARNING, not a throw: a hard mint-time failure would convert a stuck upload into a
 * rejected one, and whether a selected video should be a chunk-stream recording at all is an open
 * product question. This only makes the disagreement visible where it originates.
 *
 * It lives on this constructor because every `/uploads` target in both apps is minted here — the
 * two response wrappers, the persisted-row re-normalizations, and the URL-refresh remint — so one
 * check cannot be missed by a new caller. Pass-throughs that re-normalize an existing target carry
 * no `transport` and are skipped.
 *
 * The `[Upload]` prefix is load-bearing for contact-admin: its Vitest console gate
 * (`apps/contact-admin/tests/setup/env.ts`) allowlists upload diagnostics by that prefix.
 */
export function createUploadsMultipartUploadTarget(input: {
	registryId: string;
	attemptId: string;
	generation: number;
	takeGeneration?: string;
	envelopes: readonly UploadTargetEnvelope[];
	transport?: string;
}): UploadsMultipartUploadTarget {
	assertNoLegacyStorageCoordinates(input);
	if (
		typeof input.transport === "string" &&
		input.transport.length > 0 &&
		input.transport !== "multipart" &&
		input.transport !== "chunked-object"
	) {
		console.warn("[Upload] Uploads facade declared a non-multipart transport for a target being wrapped as multipart", {
			registryId: input.registryId,
			attemptId: input.attemptId,
			generation: input.generation,
			declaredTransport: input.transport
		});
	}
	assertNonBlank(input.registryId, "registryId");
	assertNonBlank(input.attemptId, "attemptId");
	assertPositiveInteger(input.generation, "generation");
	if (!Array.isArray(input.envelopes) || input.envelopes.length === 0) throw new Error("Upload target requires at least one envelope");
	const normalizedEnvelopes = input.envelopes.map((envelope, index) =>
		validateUploadTargetEnvelope(envelope, index + 1, input.attemptId, input.generation)
	);
	const targetIds = new Set<string>();
	for (const normalizedEnvelope of normalizedEnvelopes) {
		if (targetIds.has(normalizedEnvelope.targetId)) throw new Error("Upload target envelopes contain a duplicate targetId");
		targetIds.add(normalizedEnvelope.targetId);
	}
	return {
		type: "multipart",
		family: "uploads",
		registryId: input.registryId,
		attemptId: input.attemptId,
		generation: input.generation,
		takeGeneration: input.takeGeneration,
		envelopes: normalizedEnvelopes,
		// Carried, not defaulted: an absent transport must stay absent so a re-normalization pass-through
		// (which never carries one) cannot silently stamp "multipart" onto a chunked-object target.
		...(input.transport === "chunked-object" ? { transport: "chunked-object" as const } : {})
	};
}

function stripCompatibilityCoordinates(target: UploadsMultipartUploadTarget): {
	registryId: string;
	attemptId: string;
	generation: number;
	takeGeneration?: string;
	envelopes: readonly UploadTargetEnvelope[];
	transport?: UploadsMintedTransport;
} {
	assertNoLegacyStorageCoordinates(target);
	return {
		registryId: target.registryId,
		attemptId: target.attemptId,
		generation: target.generation,
		takeGeneration: target.takeGeneration,
		envelopes: target.envelopes,
		// Every adapter `normalize` re-mints through the constructor with this object. Dropping the
		// transport here would turn a resumed/normalized chunked-object target back into a multipart one,
		// and its completion would post a parts body the server refuses.
		...(target.transport ? { transport: target.transport } : {})
	};
}

export interface LegacyUploadTargetAdapter {
	readonly family: "legacy";
	normalize(target: MultipartUploadTarget | FamilyAwareMultipartUploadTarget): LegacyMultipartUploadTarget;
	/** Cheap accessor: reads the target's own chunk URLs. Never validates or normalizes. */
	chunkUrls(target: MultipartUploadTarget | LegacyMultipartUploadTarget): readonly string[];
	/** Cheap accessor: the legacy family carries no server-signed headers. Never validates or normalizes. */
	chunkHeaders(target: MultipartUploadTarget | LegacyMultipartUploadTarget): readonly Readonly<Record<string, string>>[];
	/** Cheap accessor: the durable dedupe/ownership identity. Never validates or normalizes. */
	opaqueId(target: MultipartUploadTarget | LegacyMultipartUploadTarget): string;
	storageCoordinates(target: MultipartUploadTarget | LegacyMultipartUploadTarget): LegacyStorageCoordinates;
	completionParts(
		target: MultipartUploadTarget | LegacyMultipartUploadTarget,
		parts: readonly UploadCompletionPart[]
	): readonly UploadCompletionPart[];
}

export interface UploadsUploadTargetAdapter {
	readonly family: "uploads";
	normalize(target: FamilyAwareMultipartUploadTarget): UploadsMultipartUploadTarget;
	/** Cheap accessor: maps the envelopes already on the target. Never validates or normalizes. */
	chunkUrls(target: UploadsMultipartUploadTarget): readonly string[];
	/** Cheap accessor: maps the envelopes' server-signed headers verbatim. Never validates or normalizes. */
	chunkHeaders(target: UploadsMultipartUploadTarget): readonly Readonly<Record<string, string>>[];
	/** Cheap accessor: the durable dedupe/ownership identity. Never validates or normalizes. */
	opaqueId(target: UploadsMultipartUploadTarget): string;
	storageCoordinates(target: UploadsMultipartUploadTarget): undefined;
	completionParts(target: UploadsMultipartUploadTarget, parts: readonly UploadCompletionPart[]): readonly UploadsCompletionPart[];
}

export type UploadTargetAdapter = LegacyUploadTargetAdapter | UploadsUploadTargetAdapter;

const legacyUploadTargetAdapter: LegacyUploadTargetAdapter = {
	family: "legacy",
	normalize(target) {
		if (isUploadsUploadTarget(target)) throw new UploadTargetFamilyMismatchError("legacy", "uploads");
		return createLegacyMultipartUploadTarget(target);
	},
	chunkUrls(target) {
		return (target as MultipartUploadTarget).chunkUrls;
	},
	chunkHeaders() {
		return [];
	},
	opaqueId(target) {
		return (target as MultipartUploadTarget).uploadId;
	},
	storageCoordinates(target) {
		const normalized = createLegacyMultipartUploadTarget(target as MultipartUploadTarget);
		return { uploadId: normalized.uploadId, s3Key: normalized.s3Key, bucket: normalized.bucket };
	},
	completionParts(_target, parts) {
		return parts.map((part) => ({ ...part }));
	}
};

const uploadsUploadTargetAdapter: UploadsUploadTargetAdapter = {
	family: "uploads",
	normalize(target) {
		if (!isUploadsUploadTarget(target)) throw new UploadTargetFamilyMismatchError("uploads", "legacy");
		return createUploadsMultipartUploadTarget(stripCompatibilityCoordinates(target));
	},
	chunkUrls(target) {
		return (target as UploadsMultipartUploadTarget).envelopes.map((envelope) => envelope.url);
	},
	chunkHeaders(target) {
		return (target as UploadsMultipartUploadTarget).envelopes.map((envelope) => envelope.headers);
	},
	opaqueId(target) {
		return (target as UploadsMultipartUploadTarget).registryId;
	},
	storageCoordinates(_target) {
		// `/uploads` resolves bucket/key/multipartUploadId on the server from registryId.
		return undefined;
	},
	completionParts(target, parts) {
		const normalized = createUploadsMultipartUploadTarget(stripCompatibilityCoordinates(target as UploadsMultipartUploadTarget));
		const envelopesByOrdinal = new Map(normalized.envelopes.map((envelope) => [envelope.ordinal, envelope]));
		return parts.map((part) => {
			const envelope = envelopesByOrdinal.get(part.PartNumber);
			if (!envelope) throw new Error(`No upload target envelope for part ${part.PartNumber}`);
			return { ...part, targetId: envelope.targetId, ordinal: envelope.ordinal } satisfies UploadsCompletionPart;
		});
	}
};

export function createUploadTargetAdapter(): LegacyUploadTargetAdapter;
export function createUploadTargetAdapter(family: "legacy"): LegacyUploadTargetAdapter;
export function createUploadTargetAdapter(family: "uploads"): UploadsUploadTargetAdapter;
export function createUploadTargetAdapter(family: UploadEndpointFamily = DEFAULT_UPLOAD_ENDPOINT_FAMILY): UploadTargetAdapter {
	if (family === "legacy") return legacyUploadTargetAdapter;
	if (family === "uploads") return uploadsUploadTargetAdapter;
	throw new Error("Upload endpoint family is invalid");
}

/**
 * The three cheap family accessors of `UploadTargetAdapter`, widened to the same input type the
 * `uploadTargetChunkUrls` / `uploadTargetChunkHeaders` / `uploadTargetOpaqueId` free functions
 * accept. The two concrete adapters satisfy this structurally (their methods are declared with
 * method syntax, so their narrower parameter types are bivariantly assignable), which lets one
 * resolved adapter serve a whole target-holding scope — `createUploadTargetAdapter` has no overload
 * taking a union family.
 */
export interface ResolvedUploadTargetAccessors {
	readonly family: UploadEndpointFamily;
	chunkUrls(target: FamilyAwareMultipartUploadTarget): readonly string[];
	chunkHeaders(target: FamilyAwareMultipartUploadTarget): readonly Readonly<Record<string, string>>[];
	opaqueId(target: FamilyAwareMultipartUploadTarget): string;
}

/**
 * Resolves the family adapter for ONE target binding. Identical family test and identical methods to
 * the three free functions above, so it is behaviour-neutral at every call site that swaps to it.
 *
 * `createUploadTargetAdapter` is a method-table lookup, not a constructor — resolving once per
 * target-holding scope is the point, and resolving more often than that costs nothing. Never hoist a
 * resolved adapter above the target it describes: the driver's `ensurePersistedSessionHasUploadTarget`
 * and `reinitializeSession` both MINT a replacement target mid-session, and an adapter carried in from
 * the predecessor target would describe the wrong family.
 */
export function resolveUploadTargetAdapter(target: FamilyAwareMultipartUploadTarget): ResolvedUploadTargetAccessors {
	return uploadTargetFamily(target) === "uploads" ? createUploadTargetAdapter("uploads") : createUploadTargetAdapter("legacy");
}

export function getUploadEndpointFamily(session: { endpointFamily?: UploadEndpointFamily }): UploadEndpointFamily {
	return session.endpointFamily === "uploads" ? "uploads" : DEFAULT_UPLOAD_ENDPOINT_FAMILY;
}

export function assertUploadTargetFamily(
	session: { endpointFamily?: UploadEndpointFamily },
	target: MultipartUploadTarget | FamilyAwareMultipartUploadTarget
): void {
	const expected = getUploadEndpointFamily(session);
	const actual = uploadTargetFamily(target);
	if (expected !== actual) throw new UploadTargetFamilyMismatchError(expected, actual);
}

export function redactUploadTarget(target: MultipartUploadTarget | FamilyAwareMultipartUploadTarget):
	| { family: "legacy"; type: "multipart"; chunkCount: number; uploadId: "[redacted]"; s3Key: "[redacted]"; bucket: "[redacted]" }
	| {
			family: "uploads";
			type: "multipart";
			chunkCount: number;
			registryId: string;
			attemptId: string;
			generation: number;
			targetIds: string[];
	  } {
	const actualFamily = "family" in target ? target.family : DEFAULT_UPLOAD_ENDPOINT_FAMILY;
	if (actualFamily === "legacy") {
		const normalized = createLegacyMultipartUploadTarget(target as MultipartUploadTarget);
		return {
			family: "legacy",
			type: "multipart",
			chunkCount: normalized.chunkUrls.length,
			uploadId: "[redacted]",
			s3Key: "[redacted]",
			bucket: "[redacted]"
		};
	}
	const normalized = createUploadsMultipartUploadTarget(stripCompatibilityCoordinates(target as UploadsMultipartUploadTarget));
	return {
		family: "uploads",
		type: "multipart",
		chunkCount: normalized.envelopes.length,
		registryId: normalized.registryId,
		attemptId: normalized.attemptId,
		generation: normalized.generation,
		targetIds: normalized.envelopes.map((envelope) => envelope.targetId)
	};
}

function assertValidMultipartTarget(target: FamilyAwareMultipartUploadTarget, expectedChunkCount: number): void {
	if (isUploadsUploadTarget(target)) {
		const normalized = createUploadsMultipartUploadTarget(stripCompatibilityCoordinates(target));
		if (normalized.envelopes.length !== expectedChunkCount)
			throw new Error(`Multipart upload target has invalid URL count for ${expectedChunkCount} chunks`);
		return;
	}
	const invalidField = getInvalidMultipartTargetIdentityField(target);
	if (invalidField) {
		throw new Error(`Multipart upload target has invalid ${invalidField}`);
	}
	if (!hasValidMultipartUrls(target.chunkUrls, expectedChunkCount) || target.chunkUrls.some((url) => !isValidPresignedUrl(url))) {
		throw new Error(`Multipart upload target has invalid URL count or contents for ${expectedChunkCount} chunks`);
	}
}

export function defaultReportInitiationFailure(category: UploadTargetCategory, error: unknown): void {
	const label = category === "video" ? "videos" : `${category}s`;
	if ((error as any)?.status === 404 || (error as any)?.code === 404) {
		// The 404 is skipped from the ERROR channel on purpose — it is the ordinary "this conversation
		// has no such target" answer and used to be logged as a failure on every call. But R-2 says this
		// status conflates absence, wrong tenant and revoked access (`types/UploadFacadeError.ts`), so
		// "presigning silently produced nothing" is exactly the shape of failure that costs hours to
		// reconstruct later. Name the skip and the reason (root `CLAUDE.md`, "Diagnostic logging when
		// fixing silent failures"); the level stays below `error` so it is still not an alarm.
		console.warn(
			`[Upload] Upload-target initiation for ${label} returned 404; not reported as a failure. The server's 404 conflates ` +
				`"no such target", "wrong tenant" and "no longer permitted", so this may be an auth-gated state rather than absence.`,
			{ category, status: (error as any)?.status ?? (error as any)?.code ?? null }
		);
		return;
	}
	console.error(`Failed to get upload targets for ${label}`, error);
}

/** How many un-aborted targets a summary names individually before it falls back to counts. */
export const ROLLBACK_SUMMARY_SAMPLE_SIZE = 10;

/**
 * The redacted, sink-ready shape of a rollback summary — what a host logs. Target identity is the
 * opaque id only (registryId for `/uploads`, uploadId for legacy), never a key, bucket, or URL.
 */
export function describeRollbackFailureSummary(summary: RollbackFailureSummary<FamilyAwareMultipartUploadTarget>): Record<string, unknown> {
	return {
		abortedCount: summary.abortedCount,
		failedCount: summary.failed.length,
		unattemptedCount: summary.unattempted.length,
		inFlightCount: summary.inFlight.length,
		elapsedMs: summary.elapsedMs,
		budgetMs: summary.budgetMs,
		maxAttempts: summary.maxAttempts,
		failures: summary.failed.slice(0, ROLLBACK_SUMMARY_SAMPLE_SIZE).map((failure) => ({
			family: uploadTargetFamily(failure.target),
			targetId: uploadTargetOpaqueId(failure.target),
			attempts: failure.attempts,
			httpStatus: failure.httpStatus ?? null
		})),
		unattempted: summary.unattempted.slice(0, ROLLBACK_SUMMARY_SAMPLE_SIZE).map((target) => uploadTargetOpaqueId(target)),
		inFlight: summary.inFlight.slice(0, ROLLBACK_SUMMARY_SAMPLE_SIZE).map((target) => uploadTargetOpaqueId(target))
	};
}

/**
 * LSP-19605: one line per rollback, at warn. The previous per-target `console.error` produced one
 * line per minted target per failed batch. Warn, not error: an un-aborted target is an orphaned
 * multipart upload that the server's incomplete-parts sweep reclaims, not a lost customer byte.
 */
export function defaultReportRollbackFailures(summary: RollbackFailureSummary<FamilyAwareMultipartUploadTarget>): void {
	console.warn(
		`[Upload] Multipart rollback left ${summary.failed.length + summary.unattempted.length + summary.inFlight.length} target(s) unconfirmed after ${summary.elapsedMs}ms — the server's incomplete-parts sweep reclaims any orphan.`,
		describeRollbackFailureSummary(summary)
	);
}

function rollbackFailureHttpStatus(error: unknown): number | undefined {
	try {
		// contact-admin's `_handleResponse` rejects `{ httpStatus, code: <numeric status> }`; the shared
		// `UploadsFacadeClient` typed error carries `status`. Read both; ignore anything outside HTTP range.
		const candidate = error as { httpStatus?: unknown; status?: unknown } | null | undefined;
		for (const value of [candidate?.httpStatus, candidate?.status]) {
			if (typeof value === "number" && Number.isSafeInteger(value) && value >= 100 && value <= 599) return value;
		}
	} catch {
		// A hostile getter cannot decide retryability; treat the failure as unclassified.
	}
	return undefined;
}

function isCancellation(error: unknown): boolean {
	try {
		const candidate = error as { name?: unknown; code?: unknown } | null | undefined;
		return candidate?.name === "AbortError" || candidate?.code === "ABORT_ERR";
	} catch {
		return false;
	}
}

/**
 * A rollback abort is retried only when the server may not have heard it: a network failure or
 * timeout (no status at all), a 5xx, a 408, or a 429. Any other 4xx is a definitive answer — a 404
 * means the target is already gone, a 409 means a fence refused it — and repeating the request
 * reproduces the same answer while spending more of the budget. A cancellation (`AbortError`) is the
 * caller withdrawing the request and is never retried.
 */
export function isRetryableRollbackFailure(error: unknown): boolean {
	if (isCancellation(error)) return false;
	const status = rollbackFailureHttpStatus(error);
	if (status === undefined) return true;
	if (status === 408 || status === 429) return true;
	return status >= 500;
}

export interface DirectUploadTarget {
	type: "direct";
	presignedUrl: string;
	/** `/uploads` direct-put targets retain only opaque registry fencing metadata. */
	family?: "uploads";
	registryId?: string;
	attemptId?: string;
	generation?: number;
	takeGeneration?: string;
	headers?: Readonly<Record<string, string>>;
}

export class DirectUploadTargetCardinalityError extends Error {
	/**
	 * Targets the sibling categories DID mint before this one came up short. Categories are
	 * initiated concurrently and each swallows its own failure into an empty array, so a single
	 * short category cannot be assumed to mean nothing was reserved. Carrying the fulfilled
	 * targets out with the error is what lets the caller roll them back instead of discarding
	 * live reservations it can no longer name.
	 */
	readonly fulfilledTargets: readonly DirectUploadTarget[];

	constructor(category: string, actualCount: number, expectedCount: number, fulfilledTargets: readonly DirectUploadTarget[] = []) {
		super(`Direct upload target response for ${category} returned ${actualCount}/${expectedCount} targets`);
		this.name = "DirectUploadTargetCardinalityError";
		this.fulfilledTargets = fulfilledTargets;
	}
}

export interface DirectTargetInitiator<FileLike extends UploadFileLike> {
	initiateFiles(request: { files: FileLike[]; category: FileUploadCategory }): Promise<DirectUploadTarget[]>;
	initiateVideos(request: { files: FileLike[] }): Promise<DirectUploadTarget[]>;
}

export interface InitiateDirectUploadTargetsOptions<FileLike extends UploadFileLike> {
	images: FileLike[];
	documents: FileLike[];
	videos: FileLike[];
	initiator: DirectTargetInitiator<FileLike>;
}

/** Initiates legacy direct targets while keeping category orchestration in the engine. */
export async function initiateDirectUploadTargets<FileLike extends UploadFileLike>(
	options: InitiateDirectUploadTargetsOptions<FileLike>
): Promise<DirectUploadTarget[]> {
	const [images, documents, videos] = await Promise.all([
		options.images.length ? options.initiator.initiateFiles({ files: options.images, category: "image" }) : [],
		options.documents.length ? options.initiator.initiateFiles({ files: options.documents, category: "document" }) : [],
		options.videos.length ? options.initiator.initiateVideos({ files: options.videos }) : []
	]);
	const categories = [
		{ label: "images", targets: images, expectedCount: options.images.length },
		{ label: "documents", targets: documents, expectedCount: options.documents.length },
		{ label: "videos", targets: videos, expectedCount: options.videos.length }
	];
	for (const category of categories) {
		if (category.targets.length !== category.expectedCount) {
			// Every target minted by ANY category rides out with the error. The short category's own
			// partial targets are included: it returned fewer than requested, but the ones it did
			// return are real reservations too, and dropping them here would leak them just as surely
			// as dropping a fulfilled sibling's.
			throw new DirectUploadTargetCardinalityError(category.label, category.targets.length, category.expectedCount, [
				...images,
				...documents,
				...videos
			]);
		}
		if (
			category.targets.some((target) => {
				if (target.type !== "direct" || !isValidPresignedUrl(target.presignedUrl)) return true;
				const familyDescriptor = Object.getOwnPropertyDescriptor(target, "family");
				if (familyDescriptor && !("value" in familyDescriptor)) return true;
				if (familyDescriptor?.value === "uploads") {
					assertNoLegacyStorageCoordinates(target);
				}
				return false;
			})
		) {
			throw new Error(`Direct upload target response for ${category.label} is malformed`);
		}
	}
	return [...images, ...documents, ...videos];
}

export interface FileTargetInitiationRequest<FileLike extends UploadFileLike> {
	file: FileLike;
	category: FileUploadCategory;
	stableId: string;
	totalChunks: number;
}

export interface VideoTargetInitiationRequest<FileLike extends UploadFileLike> {
	file: FileLike;
	stableId: string;
	totalChunks: number;
}

/** Endpoint boundary for multipart initiation and best-effort rollback. */
export interface UploadTargetInitiator<
	FileLike extends UploadFileLike,
	Target extends FamilyAwareMultipartUploadTarget = MultipartUploadTarget
> {
	initiateFile(request: FileTargetInitiationRequest<FileLike>): Promise<Target>;
	initiateVideo(request: VideoTargetInitiationRequest<FileLike>): Promise<Target>;
	abortFile(target: Target): Promise<void>;
	abortVideo(target: Target): Promise<void>;
	selectInitiationFailure?(errors: readonly unknown[]): unknown;
	reportInitiationFailure?(category: UploadTargetCategory, error: unknown): void;
	/**
	 * LSP-19605: called at most ONCE per rollback, and only when at least one target was not confirmed
	 * aborted (a failure after every attempt the budget allowed, or never attempted because the budget
	 * ran out). Replaces the per-target `reportAbortFailure`, which produced one error line per minted
	 * target per failed batch.
	 */
	reportRollbackFailures?(summary: RollbackFailureSummary<Target>): void;
}

export interface InitiateUploadTargetsOptions<FileLike extends UploadFileLike, Target extends FamilyAwareMultipartUploadTarget> {
	images: FileLike[];
	documents: FileLike[];
	videos: FileLike[];
	initiator: UploadTargetInitiator<FileLike, Target>;
	chunkSize: number;
	createStableId: () => string;
	concurrency?: number;
	/** LSP-19605 rollback pacing overrides; see `ROLLBACK_*`. Production callers omit this. */
	rollback?: RollbackPacingOptions;
}

type CategoryOutcome<Target> = {
	category: UploadTargetCategory;
	targets: Target[];
	errors: unknown[];
};

async function initiateCategory<FileLike extends UploadFileLike, Target extends FamilyAwareMultipartUploadTarget>(
	category: UploadTargetCategory,
	files: FileLike[],
	options: InitiateUploadTargetsOptions<FileLike, Target>,
	concurrency: number
): Promise<CategoryOutcome<Target>> {
	const targets = new Array<Target>(files.length);
	let nextIndex = 0;
	let failed = false;
	const errors: unknown[] = [];
	const worker = async (): Promise<void> => {
		while (nextIndex < files.length && !failed) {
			const index = nextIndex++;
			const file = files[index];
			try {
				if (!Number.isFinite(file.size) || file.size <= 0) {
					throw new Error("Multipart upload requires a positive finite file size");
				}
				const stableId = options.createStableId();
				const totalChunks = requiredMultipartParts(file.size, options.chunkSize);
				if (exceedsMultipartPartLimit(file.size, options.chunkSize)) {
					throw new Error(
						`Multipart upload requires ${totalChunks} parts, exceeding S3's ${MAX_MULTIPART_PARTS.toLocaleString("en-US")}-part limit`
					);
				}
				const target =
					category === "video"
						? await options.initiator.initiateVideo({ file, stableId, totalChunks })
						: await options.initiator.initiateFile({
								file,
								category,
								stableId,
								totalChunks
							});
				targets[index] = target;
				assertValidMultipartTarget(target, totalChunks);
			} catch (error) {
				errors.push(error);
				failed = true;
			}
		}
	};

	await Promise.all(Array.from({ length: Math.min(concurrency, files.length) }, () => worker()));
	if (errors.length > 0) {
		try {
			options.initiator.reportInitiationFailure?.(category, options.initiator.selectInitiationFailure?.(errors) ?? errors[0]);
		} catch {
			// Failure observers must not replace the initiation error or prevent sibling rollback.
		}
	}
	return {
		category,
		targets: targets.filter((target): target is Target => target !== undefined),
		errors
	};
}

function positiveIntegerOr(value: number | undefined, fallback: number): number {
	const candidate = Math.floor(value ?? fallback);
	return Number.isFinite(candidate) && candidate > 0 ? candidate : fallback;
}

function sleep(delayMs: number): Promise<void> {
	return new Promise((resolve) => setTimeout(resolve, delayMs));
}

type RollbackEntry<Target> = {
	category: UploadTargetCategory;
	target: Target;
	attempts: number;
	state: "pending" | "in-flight" | "aborted" | "failed";
	lastError: unknown;
};

/**
 * Aborts every minted target after an initiation failure. Best effort, paced per the LSP-19605
 * contract documented on `ROLLBACK_ABORT_MAX_ATTEMPTS`. Never throws — the initiation error is the
 * verdict.
 *
 * Shape: ROUNDS, not per-target loops. Round 0 gives every target its first attempt at bounded
 * concurrency before any target is retried; each later round waits one shared backoff delay and
 * re-attempts only the retryable failures of the round before. A per-target retry loop would let
 * the first few targets spend three attempts and two backoffs each while holding a concurrency slot,
 * so under a fast-failing API many targets would reach the budget without ever being tried once —
 * and "every target gets one abort request" is the property that matters most here.
 *
 * The summary fires exactly once: when the rollback settles, or at the budget if it has not — in
 * which case attempts still in flight are counted as such rather than waited for (an abort request
 * has no cancellation signal, so waiting could mean never).
 */
async function abortTargetsBestEffort<Target extends FamilyAwareMultipartUploadTarget>(
	outcomes: CategoryOutcome<Target>[],
	initiator: UploadTargetInitiator<UploadFileLike, Target>,
	concurrency: number,
	pacing: RollbackPacingOptions = {}
): Promise<void> {
	const entries: RollbackEntry<Target>[] = outcomes.flatMap((outcome) =>
		outcome.targets.map((target) => ({
			category: outcome.category,
			target,
			attempts: 0,
			state: "pending" as const,
			lastError: undefined
		}))
	);
	if (entries.length === 0) return;
	const budgetMs = positiveIntegerOr(pacing.budgetMs, ROLLBACK_BUDGET_MS);
	const maxAttempts = positiveIntegerOr(pacing.maxAttempts, ROLLBACK_ABORT_MAX_ATTEMPTS);
	const baseDelayMs = positiveIntegerOr(pacing.baseDelayMs, ROLLBACK_ABORT_BASE_DELAY_MS);
	const maxDelayMs = Math.max(baseDelayMs, positiveIntegerOr(pacing.maxDelayMs, ROLLBACK_ABORT_MAX_DELAY_MS));
	const startedAt = Date.now();
	const deadline = startedAt + budgetMs;
	let summaryEmitted = false;

	const emitSummary = (): void => {
		if (summaryEmitted) return;
		summaryEmitted = true;
		const failed: RollbackAbortFailure<Target>[] = [];
		const unattempted: Target[] = [];
		const inFlight: Target[] = [];
		let abortedCount = 0;
		for (const entry of entries) {
			if (entry.state === "aborted") abortedCount++;
			else if (entry.state === "in-flight") inFlight.push(entry.target);
			else if (entry.attempts === 0) unattempted.push(entry.target);
			else
				failed.push({
					target: entry.target,
					error: entry.lastError,
					attempts: entry.attempts,
					httpStatus: rollbackFailureHttpStatus(entry.lastError)
				});
		}
		if (failed.length === 0 && unattempted.length === 0 && inFlight.length === 0) return;
		try {
			initiator.reportRollbackFailures?.({
				abortedCount,
				failed,
				unattempted,
				inFlight,
				elapsedMs: Date.now() - startedAt,
				budgetMs,
				maxAttempts
			});
		} catch {
			// Failure observers must not replace the initiation error or prevent sibling rollback.
		}
	};

	const rollback = (async () => {
		let pending = entries;
		for (let round = 0; round < maxAttempts && pending.length > 0; round++) {
			if (round > 0) {
				// `nowMs: 0` turns the shared jittered curve into a bare delay. Sleep only while the budget
				// still has room for the delay AND a request after it, and re-check on waking: a
				// background-tab timer can fire long after it was due.
				const delayMs = computeRetryAt({ attempt: round - 1, nowMs: 0, baseDelayMs, maxDelayMs }) ?? baseDelayMs;
				if (Date.now() + delayMs >= deadline) break;
				await sleep(delayMs);
				if (Date.now() >= deadline) break;
			}
			const retryable: RollbackEntry<Target>[] = [];
			await processWithConcurrency(pending, concurrency, async (entry) => {
				if (Date.now() >= deadline) return;
				entry.attempts++;
				entry.state = "in-flight";
				try {
					if (entry.category === "video") await initiator.abortVideo(entry.target);
					else await initiator.abortFile(entry.target);
					entry.state = "aborted";
				} catch (error) {
					entry.lastError = error;
					if (isRetryableRollbackFailure(error) && entry.attempts < maxAttempts) {
						entry.state = "pending";
						retryable.push(entry);
					} else {
						entry.state = "failed";
					}
				}
			});
			pending = retryable;
		}
		// A retry the budget cut short is a failure with the last error it saw, not an in-flight attempt.
		for (const entry of pending) if (entry.state === "pending" && entry.attempts > 0) entry.state = "failed";
	})().then(emitSummary);

	// Release the caller when the rollback finishes OR the budget runs out, whichever is first. Past
	// the budget no new attempt starts; attempts already in flight settle silently in the background.
	let budgetTimer: ReturnType<typeof setTimeout> | undefined;
	const budget = new Promise<void>((resolve) => {
		budgetTimer = setTimeout(() => {
			emitSummary();
			resolve();
		}, budgetMs);
	});
	try {
		await Promise.race([rollback, budget]);
	} finally {
		if (budgetTimer !== undefined) clearTimeout(budgetTimer);
	}
}

/**
 * Initiates image, document, and video categories concurrently. Each category is
 * bounded independently and output remains grouped as images, documents, videos.
 */
export async function initiateUploadTargets<
	FileLike extends UploadFileLike,
	Target extends FamilyAwareMultipartUploadTarget = MultipartUploadTarget
>(options: InitiateUploadTargetsOptions<FileLike, Target>): Promise<Target[]> {
	if (!Number.isSafeInteger(options.chunkSize) || options.chunkSize <= 0) {
		throw new Error("Multipart upload requires a positive integer chunk size");
	}
	// A non-finite concurrency (e.g. NaN) must not silently produce zero workers — that would
	// resolve [] without initiating (or failing) any file. Fall back to the default instead.
	const requestedConcurrency = Math.floor(options.concurrency ?? INITIATE_CONCURRENCY);
	const concurrency = Number.isFinite(requestedConcurrency) ? Math.max(1, requestedConcurrency) : INITIATE_CONCURRENCY;
	const groups = [
		{ category: "image" as const, files: options.images },
		{ category: "document" as const, files: options.documents },
		{ category: "video" as const, files: options.videos }
	];
	const outcomes = await Promise.all(groups.map(({ category, files }) => initiateCategory(category, files, options, concurrency)));
	const failures = outcomes.flatMap((outcome) => outcome.errors);
	const rollbackInitiator = options.initiator as UploadTargetInitiator<UploadFileLike, Target>;
	if (failures.length > 0) {
		await abortTargetsBestEffort(outcomes, rollbackInitiator, concurrency, options.rollback);
		throw options.initiator.selectInitiationFailure?.(failures) ?? failures[0];
	}
	const targets = outcomes.flatMap((outcome) => outcome.targets);
	const targetIdentities = new Set<string>();
	for (const target of targets) {
		const targetIdentity = uploadTargetOpaqueId(target);
		if (targetIdentities.has(targetIdentity)) {
			await abortTargetsBestEffort(outcomes, rollbackInitiator, concurrency, options.rollback);
			throw new Error("Multipart upload targets contain a duplicate identity");
		}
		targetIdentities.add(targetIdentity);
	}
	return targets;
}
